/**
 * RepromptModal Component
 *
 * Modal for editing prompt and selecting models to create a new comparison.
 * Allows users to reprompt from an existing comparison.
 */

import React, { useState, useEffect } from 'react';
import { logger } from '../../../lib/logging';
import { getModelLogo } from '../../../utils/logoMapping';
import { useModels } from '../../../hooks/useModels';
import { MODEL_CONFIG } from '../../../utils/modelResolver';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Map database model ID to friendly ID for backend API
 * Examples:
 * - "model-openai-gpt-4o-mini" → "gpt-4"
 * - "k6wstgv39u0isb1gt922xqzc" → "deepseek"
 * - "model-xai-grok-3-mini" → "grok"
 */
function mapDbIdToFriendlyId(dbModelId: string, displayName: string): string {
  // Try to find the friendly ID from MODEL_CONFIG
  for (const [friendlyId, config] of Object.entries(MODEL_CONFIG)) {
    // Check if this database ID matches the pattern
    if (config.dbModelIdPattern.test(dbModelId)) {
      return friendlyId;
    }
    // Fallback: check if displayName matches
    if (config.displayName.toLowerCase() === displayName.toLowerCase()) {
      return friendlyId;
    }
  }

  // If no mapping found, return the dbModelId as-is (backend has some fallbacks)
  console.warn(`[RepromptModal] No friendly ID mapping found for: "${dbModelId}" (displayName: "${displayName}")`);
  return dbModelId;
}

export interface RepromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPrompt: string;
  initialSelectedModels?: string[];
  onSubmit: (comparisonId: string, prompt: string, selectedModels: string[]) => void;
}

export const RepromptModal: React.FC<RepromptModalProps> = ({
  isOpen,
  onClose,
  initialPrompt,
  initialSelectedModels = [],
  onSubmit
}) => {
  const { models: dbModels, loading: modelsLoading, error: modelsError } = useModels();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt, isOpen]);

  useEffect(() => {
    // Debug logging to diagnose selection issues
    console.log('[RepromptModal] useEffect triggered:', {
      isOpen,
      initialSelectedModelsCount: initialSelectedModels.length,
      initialSelectedModels,
      dbModelsCount: dbModels.length,
      dbModelIds: dbModels.map(m => ({
        id: m.id,
        displayName: m.displayName,
        providerName: m.provider.name
      }))
    });

    if (isOpen && initialSelectedModels.length > 0) {
      // Map short-form model IDs using the central MODEL_CONFIG
      // This uses the same mapping logic as the rest of the site
      const mappedModelIds = initialSelectedModels
        .map(shortId => {
          console.log(`[RepromptModal] Attempting to map: "${shortId}"`);

          // Try exact match first (for full IDs that might be passed)
          const exactMatch = dbModels.find(m => m.id === shortId);
          if (exactMatch) {
            console.log(`[RepromptModal] ✓ Exact match found: "${shortId}" → "${exactMatch.id}"`);
            return exactMatch.id;
          }

          // Use MODEL_CONFIG to find the matching model
          const config = MODEL_CONFIG[shortId];
          if (config) {
            // Find model whose ID matches the dbModelIdPattern
            const patternMatch = dbModels.find(m => config.dbModelIdPattern.test(m.id));
            if (patternMatch) {
              console.log(`[RepromptModal] ✓ Pattern match found: "${shortId}" → "${patternMatch.id}" (via MODEL_CONFIG)`);
              return patternMatch.id;
            }

            // Fallback: Match by displayName for edge cases (e.g., DeepSeek with non-standard ID)
            const displayNameMatch = dbModels.find(m =>
              m.displayName.toLowerCase() === config.displayName.toLowerCase()
            );
            if (displayNameMatch) {
              console.log(`[RepromptModal] ✓ DisplayName match found: "${shortId}" → "${displayNameMatch.id}" (via displayName: "${config.displayName}")`);
              return displayNameMatch.id;
            }
          }

          // Fallback: substring match for backwards compatibility
          const substringMatch = dbModels.find(m =>
            m.id.toLowerCase().includes(shortId.toLowerCase())
          );
          if (substringMatch) {
            console.log(`[RepromptModal] ✓ Substring match found: "${shortId}" → "${substringMatch.id}"`);
            return substringMatch.id;
          }

          console.log(`[RepromptModal] ✗ No match found for: "${shortId}"`);
          return undefined;
        })
        .filter((id): id is string => id !== undefined);

      console.log('[RepromptModal] Final mapped model IDs:', mappedModelIds);
      setSelectedModels(mappedModelIds);
    } else if (isOpen) {
      console.log('[RepromptModal] Clearing selected models');
      setSelectedModels([]);
    }
  }, [isOpen, initialSelectedModels, dbModels]);

  // Transform DB models to local format and set initial selection
  const AVAILABLE_MODELS = dbModels.map(model => ({
    id: model.id,
    name: model.displayName,
    provider: model.provider.name
  }));
  const totalAvailableModels = AVAILABLE_MODELS.length;
  const allModelsSelected = totalAvailableModels > 0 && selectedModels.length === totalAvailableModels;
  const canToggleAllModels = totalAvailableModels > 0 && !modelsLoading;

  const handleToggleModel = (modelId: string) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    );
  };

  const handleToggleAllModels = () => {
    if (!canToggleAllModels) {
      return;
    }

    setSelectedModels(prev =>
      prev.length === totalAvailableModels
        ? []
        : AVAILABLE_MODELS.map(model => model.id)
    );
  };

  const handleSubmit = async () => {
    if (prompt.trim() && selectedModels.length > 0) {
      try {
        if (!user) {
          setError('Please sign in to create a comparison.');
          return;
        }

        setLoading(true);
        setError(null);

        logger.info('reprompt_submit_start', undefined, {
          op: 'reprompt_submit_start',
          component: 'RepromptModal',
          promptLength: prompt.length,
          modelCount: selectedModels.length
        });

        // Map database IDs to friendly IDs for backend
        const friendlyModelIds = selectedModels.map(dbId => {
          const model = dbModels.find(m => m.id === dbId);
          if (!model) {
            console.warn(`[RepromptModal] Model not found for ID: ${dbId}`);
            return dbId;
          }
          return mapDbIdToFriendlyId(dbId, model.displayName);
        });

        console.log('[RepromptModal] Sending to backend:', {
          dbModelIds: selectedModels,
          friendlyModelIds,
          mapping: selectedModels.map((dbId, i) => `${dbId} → ${friendlyModelIds[i]}`)
        });

        // Call backend API to create comparison (same as ComparisonSetupPage)
        const idToken = await user.getIdToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }

        const response = await fetch('/api/comparison/create', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: prompt,
            models: friendlyModelIds  // Use friendly IDs like ComparisonSetupPage
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        logger.info('reprompt_submit_success', undefined, {
          op: 'reprompt_submit_success',
          component: 'RepromptModal',
          comparisonId: data.comparisonId,
          modelCount: selectedModels.length
        });

        // Call the onSubmit callback with success data
        onSubmit(data.comparisonId, prompt, selectedModels);
        onClose();

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create comparison';
        setError(errorMessage);

        logger.error('reprompt_submit_error', undefined, {
          op: 'reprompt_submit_error',
          component: 'RepromptModal',
          error: errorMessage
        });

        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    logger.info('reprompt_cancel', undefined, {
      op: 'reprompt_cancel',
      component: 'RepromptModal'
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="min-h-screen flex items-center justify-center p-4">
        <div
          className="relative bg-slate-800 rounded-xl shadow-2xl max-w-2xl w-full border border-slate-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/images/Reprompt-button.png"
                alt="Reprompt icon"
                className="w-6 h-6"
              />
              <h2 className="text-xl font-bold text-white">Reprompt</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Loading State */}
            {modelsLoading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                <p className="text-slate-400 text-sm">Loading available models...</p>
              </div>
            )}

            {/* Error State */}
            {modelsError && (
              <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold mb-1">Failed to load models</h3>
                <p className="text-slate-300 text-sm">{modelsError}</p>
                <button
                  onClick={onClose}
                  className="mt-3 px-4 py-2 bg-red-700/30 hover:bg-red-700/50 rounded-lg text-red-200 text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            )}

            {/* Submission Error State */}
            {error && (
              <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold mb-1">Failed to create comparison</h3>
                <p className="text-slate-300 text-sm">{error}</p>
              </div>
            )}

            {/* Main Content (only show when models loaded) */}
            {!modelsLoading && !modelsError && (
              <>
            {/* Prompt Textarea */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                placeholder="Enter your prompt..."
              />
            </div>

            {/* Model Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Models
              </label>
              <div className="grid grid-cols-2 gap-3">
                {AVAILABLE_MODELS.map((model) => (
                  <label
                    key={model.id}
                    className="flex items-center space-x-3 p-3 rounded-lg bg-slate-900/30 border border-slate-700/50 cursor-pointer hover:bg-slate-900/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedModels.includes(model.id)}
                      onChange={() => handleToggleModel(model.id)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <div className="w-8 h-8 bg-slate-700/30 rounded-lg flex items-center justify-center p-1 flex-shrink-0">
                      <img
                        src={getModelLogo(model.id, model.name)}
                        alt={`${model.name} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">
                        {model.name}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-3 text-right">
                <button
                  type="button"
                  onClick={handleToggleAllModels}
                  disabled={!canToggleAllModels}
                  className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {allModelsSelected ? 'Deselect all models' : 'Select all models'}
                </button>
              </div>
            </div>
              </>
            )}
          </div>

          {/* Footer */}
          {!modelsLoading && !modelsError && (
            <div className="px-6 py-4 bg-slate-900/30 border-t border-slate-700/50 flex items-center justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm font-medium text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!prompt.trim() || selectedModels.length === 0 || loading}
                className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {loading ? 'Creating comparison...' : 'Create new comparison'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
